import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Utensils, 
  Palette, 
  Sparkles, 
  Plus, 
  X, 
  Clock, 
  ChefHat,
  Baby,
  Loader2,
  Lightbulb,
  AlertCircle,
  Heart,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveRecipe, getSavedRecipes } from './SavedRecipes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ageRanges = [
  { value: 'toddler (1-3)', label: 'Toddler (1-3 years)' },
  { value: 'preschool (3-5)', label: 'Preschool (3-5 years)' },
  { value: 'elementary (6-10)', label: 'Elementary (6-10 years)' },
  { value: 'preteen (11-13)', label: 'Preteen (11-13 years)' },
];

const mealTypes = [
  { value: 'any', label: 'Any meal' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' },
];

// Helper to check if recipe is saved
const isRecipeSaved = (recipeName) => {
  const saved = getSavedRecipes();
  return saved.some(r => r.name.toLowerCase() === recipeName.toLowerCase());
};

// Meal Ideas Section
const MealIdeasSection = () => {
  const [ingredients, setIngredients] = useState([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [mealType, setMealType] = useState('any');
  const [dietaryRestrictions, setDietaryRestrictions] = useState('');
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [error, setError] = useState(null);
  const [savedRecipes, setSavedRecipes] = useState(getSavedRecipes());

  const addIngredient = () => {
    if (newIngredient.trim() && !ingredients.includes(newIngredient.trim())) {
      setIngredients([...ingredients, newIngredient.trim()]);
      setNewIngredient('');
    }
  };

  const removeIngredient = (ing) => {
    setIngredients(ingredients.filter(i => i !== ing));
  };

  const handleSaveRecipe = (idea) => {
    saveRecipe({
      ...idea,
      type: 'meal',
    });
    setSavedRecipes(getSavedRecipes());
    toast.success(`"${idea.name}" saved to your recipes!`);
  };

  const isAlreadySaved = (name) => {
    return savedRecipes.some(r => r.name.toLowerCase() === name.toLowerCase());
  };

  const generateIdeas = async () => {
    if (ingredients.length === 0) {
      toast.error('Please add at least one ingredient');
      return;
    }

    setLoading(true);
    setError(null);
    setIdeas([]);

    try {
      const response = await axios.post(`${API}/resources/meal-ideas`, {
        ingredients,
        meal_type: mealType,
        dietary_restrictions: dietaryRestrictions ? dietaryRestrictions.split(',').map(s => s.trim()) : [],
      });
      
      setIdeas(response.data.ideas || []);
      toast.success('Meal ideas generated!');
    } catch (err) {
      console.error('Error generating meal ideas:', err);
      setError('Failed to generate ideas. Please try again.');
      toast.error('Failed to generate meal ideas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 relative overflow-hidden">
        {/* Decorative corner */}
        <div className="absolute top-2 right-2 text-xl opacity-25">🥘</div>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-brown/10 flex items-center justify-center border border-brown/20">
            <ChefHat className="w-5 h-5 text-brown" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-ink-900">What&apos;s in your kitchen?</h3>
            <p className="text-sm text-ink-500 font-accent italic">Add ingredients you have on hand</p>
          </div>
        </div>

        {/* Ingredient input */}
        <div className="flex gap-2 mb-4">
          <Input
            data-testid="meal-ingredient-input"
            value={newIngredient}
            onChange={(e) => setNewIngredient(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addIngredient()}
            placeholder="e.g., chicken, rice, tomatoes..."
            className="flex-1 border-stone-300 focus:border-brown focus:ring-brown/20"
          />
          <Button
            data-testid="meal-add-ingredient"
            onClick={addIngredient}
            variant="outline"
            size="icon"
            className="border-sage text-sage hover:bg-sage-mist"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Ingredient tags */}
        {ingredients.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {ingredients.map((ing, idx) => (
              <Badge
                key={idx}
                className="bg-sage/20 text-sage-dark hover:bg-sage/30 cursor-pointer border border-sage/30"
                onClick={() => removeIngredient(ing)}
              >
                {ing}
                <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}

        {/* Options */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-ink-500 mb-1 block font-accent italic">Meal type</label>
            <Select value={mealType} onValueChange={setMealType}>
              <SelectTrigger data-testid="meal-type-select" className="border-stone-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mealTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-ink-500 mb-1 block font-accent italic">Dietary needs</label>
            <Input
              data-testid="meal-dietary-input"
              value={dietaryRestrictions}
              onChange={(e) => setDietaryRestrictions(e.target.value)}
              placeholder="vegetarian, gluten-free..."
              className="border-stone-300 focus:border-brown"
            />
          </div>
        </div>

        <Button
          data-testid="meal-generate-btn"
          onClick={generateIdeas}
          disabled={loading || ingredients.length === 0}
          className="w-full vintage-btn btn-brown rounded-lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating ideas...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Meal Ideas
            </>
          )}
        </Button>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="p-4 bg-survival-bg border border-survival-border">
          <div className="flex items-center gap-2 text-survival-text">
            <AlertCircle className="w-5 h-5" />
            <p className="font-accent">{error}</p>
          </div>
        </Card>
      )}

      {/* Results */}
      {ideas.length > 0 && (
        <div className="space-y-4 animate-fade-in">
          <h4 className="font-serif font-medium text-ink-900 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-brown" />
            Your meal ideas
          </h4>
          {ideas.map((idea, idx) => (
            <Card
              key={idx}
              data-testid={`meal-idea-${idx}`}
              className="p-5 bg-paper-light shadow-lifted hover:shadow-lifted-lg transition-all border border-stone-200 recipe-card"
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-serif text-lg text-ink-900">{idea.name}</h4>
                <Button
                  data-testid={`save-meal-${idx}`}
                  onClick={() => handleSaveRecipe(idea)}
                  disabled={isAlreadySaved(idea.name)}
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 ${isAlreadySaved(idea.name) ? 'text-survival-text' : 'text-ink-400 hover:text-survival-text'}`}
                >
                  <Heart className={`w-4 h-4 ${isAlreadySaved(idea.name) ? 'fill-current' : ''}`} />
                </Button>
              </div>
              <p className="text-ink-700 mb-3 font-accent italic">{idea.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge className="bg-brown/10 text-brown border border-brown/20">
                  <Clock className="w-3 h-3 mr-1" />
                  {idea.prep_time}
                </Badge>
                <Badge className="bg-sage/10 text-sage-dark border border-sage/20">
                  {idea.difficulty}
                </Badge>
                {isAlreadySaved(idea.name) && (
                  <Badge className="bg-survival-bg text-survival-text border border-survival-border">
                    <Heart className="w-3 h-3 mr-1 fill-current" />
                    Saved
                  </Badge>
                )}
              </div>
              
              <div>
                <p className="text-xs text-ink-500 uppercase tracking-wide mb-2 font-accent">Ingredients needed</p>
                <div className="flex flex-wrap gap-1">
                  {idea.ingredients_needed?.map((ing, i) => (
                    <span key={i} className="text-sm text-ink-900 bg-paper-aged px-2 py-1 rounded border border-stone-200">
                      {ing}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Craft Ideas Section
const CraftIdeasSection = () => {
  const [ageRange, setAgeRange] = useState('preschool (3-5)');
  const [timeAvailable, setTimeAvailable] = useState('30 minutes');
  const [materials, setMaterials] = useState('');
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [error, setError] = useState(null);
  const [savedRecipes, setSavedRecipes] = useState(getSavedRecipes());

  const handleSaveCraft = (idea) => {
    saveRecipe({
      ...idea,
      type: 'craft',
    });
    setSavedRecipes(getSavedRecipes());
    toast.success(`"${idea.name}" saved to your ideas!`);
  };

  const isAlreadySaved = (name) => {
    return savedRecipes.some(r => r.name.toLowerCase() === name.toLowerCase());
  };

  const generateIdeas = async () => {
    setLoading(true);
    setError(null);
    setIdeas([]);

    try {
      const response = await axios.post(`${API}/resources/craft-ideas`, {
        age_range: ageRange,
        time_available: timeAvailable,
        materials_available: materials ? materials.split(',').map(s => s.trim()) : [],
      });
      
      setIdeas(response.data.ideas || []);
      toast.success('Craft ideas generated!');
    } catch (err) {
      console.error('Error generating craft ideas:', err);
      setError('Failed to generate ideas. Please try again.');
      toast.error('Failed to generate craft ideas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 relative overflow-hidden">
        {/* Decorative corner */}
        <div className="absolute top-2 right-2 text-xl opacity-25">🎨</div>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20">
            <Baby className="w-5 h-5 text-sage" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-ink-900">Craft time!</h3>
            <p className="text-sm text-ink-500 font-accent italic">Get creative activity ideas for your kids</p>
          </div>
        </div>

        {/* Age range */}
        <div className="mb-4">
          <label className="text-sm text-ink-500 mb-1 block font-accent italic">Child&apos;s age range</label>
          <Select value={ageRange} onValueChange={setAgeRange}>
            <SelectTrigger data-testid="craft-age-select" className="border-stone-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ageRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Time available */}
        <div className="mb-4">
          <label className="text-sm text-ink-500 mb-1 block font-accent italic">Time available</label>
          <Select value={timeAvailable} onValueChange={setTimeAvailable}>
            <SelectTrigger data-testid="craft-time-select" className="border-stone-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15 minutes">15 minutes (quick activity)</SelectItem>
              <SelectItem value="30 minutes">30 minutes</SelectItem>
              <SelectItem value="1 hour">1 hour</SelectItem>
              <SelectItem value="2 hours">2+ hours (project)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Materials */}
        <div className="mb-4">
          <label className="text-sm text-ink-500 mb-1 block font-accent italic">Materials you have (optional)</label>
          <Input
            data-testid="craft-materials-input"
            value={materials}
            onChange={(e) => setMaterials(e.target.value)}
            placeholder="paper, markers, glue, scissors..."
            className="border-stone-300 focus:border-sage"
          />
        </div>

        <Button
          data-testid="craft-generate-btn"
          onClick={generateIdeas}
          disabled={loading}
          className="w-full vintage-btn btn-sage rounded-lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating ideas...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Craft Ideas
            </>
          )}
        </Button>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="p-4 bg-survival-bg border border-survival-border">
          <div className="flex items-center gap-2 text-survival-text">
            <AlertCircle className="w-5 h-5" />
            <p className="font-accent">{error}</p>
          </div>
        </Card>
      )}

      {/* Results */}
      {ideas.length > 0 && (
        <div className="space-y-4 animate-fade-in">
          <h4 className="font-serif font-medium text-ink-900 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-sage" />
            Fun craft ideas
          </h4>
          {ideas.map((idea, idx) => (
            <Card
              key={idx}
              data-testid={`craft-idea-${idx}`}
              className="p-5 bg-paper-light shadow-lifted hover:shadow-lifted-lg transition-all border border-stone-200 recipe-card"
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-serif text-lg text-ink-900">{idea.name}</h4>
                <Button
                  data-testid={`save-craft-${idx}`}
                  onClick={() => handleSaveCraft(idea)}
                  disabled={isAlreadySaved(idea.name)}
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 ${isAlreadySaved(idea.name) ? 'text-sage' : 'text-ink-400 hover:text-sage'}`}
                >
                  <Heart className={`w-4 h-4 ${isAlreadySaved(idea.name) ? 'fill-current' : ''}`} />
                </Button>
              </div>
              <p className="text-ink-700 mb-3 font-accent italic">{idea.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge className="bg-sage/10 text-sage-dark border border-sage/20">
                  <Clock className="w-3 h-3 mr-1" />
                  {idea.time_required}
                </Badge>
                <Badge className="bg-brown/10 text-brown border border-brown/20">
                  {idea.age_appropriate}
                </Badge>
                {isAlreadySaved(idea.name) && (
                  <Badge className="bg-sage/20 text-sage-dark border border-sage/30">
                    <Heart className="w-3 h-3 mr-1 fill-current" />
                    Saved
                  </Badge>
                )}
              </div>
              
              <div className="mb-4">
                <p className="text-xs text-ink-500 uppercase tracking-wide mb-2 font-accent">Materials needed</p>
                <div className="flex flex-wrap gap-1">
                  {idea.materials_needed?.map((mat, i) => (
                    <span key={i} className="text-sm text-ink-900 bg-paper-aged px-2 py-1 rounded border border-stone-200">
                      {mat}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-xs text-ink-500 uppercase tracking-wide mb-2 font-accent">Instructions</p>
                <ol className="list-decimal list-inside space-y-1">
                  {idea.instructions?.map((step, i) => (
                    <li key={i} className="text-sm text-ink-900">{step}</li>
                  ))}
                </ol>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Resources Component
const Resources = () => {
  const [activeTab, setActiveTab] = useState('meals');
  const navigate = useNavigate();
  const savedCount = getSavedRecipes().length;

  return (
    <div className="space-y-6 relative" data-testid="resources-page">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 text-3xl opacity-20 pointer-events-none">✨</div>
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Resources
          </h2>
          <p className="text-ink-500 font-accent italic">
            AI-powered ideas for meals & activities 🌿
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-brown/10 flex items-center justify-center border border-brown/20">
          <Sparkles className="w-6 h-6 text-brown" />
        </div>
      </div>

      {/* Saved recipes link */}
      {savedCount > 0 && (
        <Card 
          className="p-4 bg-survival-bg/50 border border-survival-border/50 cursor-pointer hover:bg-survival-bg transition-colors"
          onClick={() => navigate('/saved-recipes')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-survival-text" />
            </div>
            <div className="flex-1">
              <p className="font-serif text-ink-900">Saved Ideas</p>
              <p className="text-xs text-ink-500 font-accent italic">
                {savedCount} {savedCount === 1 ? 'idea' : 'ideas'} saved
              </p>
            </div>
            <Heart className="w-5 h-5 text-survival-text" />
          </div>
        </Card>
      )}

      {/* Tab selector - Bookmark style */}
      <div className="flex gap-0 border-b border-stone-200">
        <button
          data-testid="resources-tab-meals"
          onClick={() => setActiveTab('meals')}
          className={`
            px-6 py-3 font-serif transition-all border-t-2 rounded-t-lg
            ${activeTab === 'meals' 
              ? 'bg-paper-light border-brown text-ink-900 border-x border-stone-200 -mb-px' 
              : 'bg-paper-aged border-transparent text-ink-500 hover:text-ink-700'
            }
          `}
        >
          <Utensils className="w-4 h-4 inline mr-2" />
          Meal Ideas
        </button>
        <button
          data-testid="resources-tab-crafts"
          onClick={() => setActiveTab('crafts')}
          className={`
            px-6 py-3 font-serif transition-all border-t-2 rounded-t-lg
            ${activeTab === 'crafts' 
              ? 'bg-paper-light border-sage text-ink-900 border-x border-stone-200 -mb-px' 
              : 'bg-paper-aged border-transparent text-ink-500 hover:text-ink-700'
            }
          `}
        >
          <Palette className="w-4 h-4 inline mr-2" />
          Craft Ideas
        </button>
      </div>

      {/* Content */}
      {activeTab === 'meals' ? <MealIdeasSection /> : <CraftIdeasSection />}

      {/* Gentle reminder */}
      <Card className="p-4 bg-warmth-sand border border-stone-300 text-center journal-entry">
        <p className="text-ink-700 font-handwritten text-xl">
          🌿 These are just ideas to spark inspiration. Feel free to adapt them to what works for your family.
        </p>
      </Card>
    </div>
  );
};

export default Resources;
